import { memo, useCallback } from 'react';

import { useCaptureException } from '@zenbusiness/application-commons-logging';
import PropTypes from 'prop-types';

import DepositMoneyModal from '#client/components/MoveMoney/DepositMoneyModal';
import { MODAL_NAMES } from '#client/constants/modals';
import {
  HOW_MUCH_STEP,
  WHICH_ACCOUNT_STEP,
  CONFIRMATION_FORM_STEP,
  IS_THAT_CORRECT_ACH_STEP,
  IS_THAT_CORRECT_WIRE_STEP
} from '#client/constants/transfer';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';

import ConfirmationForm from './ConfirmationForm';
import HowMuchForm from './HowMuchForm';
import IsThisCorrectFormACH from './IsThisCorrectFormACH';
import IsThisCorrectFormWire from './IsThisCorrectFormWire';
import WhichAccountForm from './WhichAccountForm';

const CurrentStep = ({
  isHandling = false,
  handleClose,
  currentFormStep,
  idempotencyKeyFromToken = null,
  openAddPayeeModal = null
}) => {
  const { createCommonErrorToast } = useToastContext();
  const { clearAllModal, openModal } = useModalContext();
  const captureException = useCaptureException();

  const handleOpenDepositModal = useCallback(() => {
    openModal({
      component: DepositMoneyModal,
      name: MODAL_NAMES.DEPOSIT_MONEY_MODAL
    });
    setTimeout(() => {
      handleClose();
    }, 1000);
  }, [handleClose, openModal]);

  const stepNotMatchHandler = () => {
    captureException(
      new Error(`Form step is not correct with currentFormStep: ${currentFormStep}`),
      {
        level: 'warning'
      }
    );
    createCommonErrorToast();
    return null;
  };

  const renderCurrentStep = () => {
    switch (true) {
      case currentFormStep === WHICH_ACCOUNT_STEP:
        return (
          <WhichAccountForm
            handleClose={handleClose}
            isHandling={isHandling}
          />
        );
      case currentFormStep === HOW_MUCH_STEP:
        return (
          <HowMuchForm
            handleClose={handleClose}
            handleOpenDepositModal={handleOpenDepositModal}
            openAddPayeeModal={openAddPayeeModal}
          />
        );

      case currentFormStep === IS_THAT_CORRECT_ACH_STEP:
        return (
          <IsThisCorrectFormACH
            handleClose={handleClose}
            handleClosePreviousModal={() => clearAllModal()}
          />
        );

      case currentFormStep === IS_THAT_CORRECT_WIRE_STEP:
        return (
          <IsThisCorrectFormWire
            handleClose={handleClose}
            idempotencyKeyFromToken={idempotencyKeyFromToken}
          />
        );

      case currentFormStep === CONFIRMATION_FORM_STEP:
        return (
          <ConfirmationForm
            handleClose={handleClose}
            handleClosePreviousModal={() => clearAllModal()}
          />
        );

      default:
        // show error toast and report error log
        return stepNotMatchHandler();
    }
  };

  return <>{renderCurrentStep()}</>;
};

const areEqual = (preProp, nextProp) =>
  preProp.isHandling === nextProp.isHandling &&
  preProp.currentFormStep === nextProp.currentFormStep &&
  preProp.idempotencyKeyFromToken === nextProp.idempotencyKeyFromToken;

CurrentStep.propTypes = {
  isHandling: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  idempotencyKeyFromToken: PropTypes.string,
  currentFormStep: PropTypes.string.isRequired,
  openAddPayeeModal: PropTypes.func
};

export default memo(CurrentStep, areEqual);
